<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon color="primary">
          mdi-account-card-outline
        </v-icon>
        <span class="px-2">{{ $t('Trip Subscriptions') }}</span>
        <v-spacer></v-spacer>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" sm="4">
            <v-select
              v-model="filterStatus"
              :items="statusFilterOptions"
              :label="$t('Filter by Status')"
              clearable
              @change="loadSubscriptions"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              v-model="filterPaymentStatus"
              :items="paymentStatusFilterOptions"
              :label="$t('Filter by Payment Status')"
              clearable
              @change="loadSubscriptions"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="search"
              :label="$t('search')"
              append-icon="mdi-magnify"
              single-line
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :headers="translatedHeaders"
        :items="subscriptions"
        :search="search"
        :footer-props="footerProps"
      >
        <template v-slot:item.user="{ item }">
          {{ item.user ? item.user.name : $t('unknown_user') }}
        </template>
        <template v-slot:item.trip="{ item }">
          <span v-if="item.trip && item.trip.route">
            {{ item.trip.route.name || item.trip.channel }}
          </span>
          <span v-else class="grey--text">{{ $t('unknown_trip') }}</span>
        </template>
        <template v-slot:item.pickup_stop="{ item }">
          {{ item.pickup_stop ? item.pickup_stop.name : '' }}
        </template>
        <template v-slot:item.dropoff_stop="{ item }">
          {{ item.dropoff_stop ? item.dropoff_stop.name : '' }}
        </template>
        <template v-slot:item.plan="{ item }">
          {{ item.subscription_plan ? item.subscription_plan.name : '' }}
        </template>
        <template v-slot:item.dates="{ item }">
          <small>{{ item.start_date }}</small> {{ $t('to') }} <small>{{ item.end_date }}</small>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip :color="getStatusColor(item.status)" dark @click="openStatusDialog(item)">
            {{ $t(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.payment_status="{ item }">
          <v-chip :color="getPaymentStatusColor(item.payment_status)" dark @click="openPaymentStatusDialog(item)">
            {{ $t(item.payment_status) }}
          </v-chip>
        </template>
        <template v-slot:item.total_price="{ item }">
          {{ item.total_price }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon
            small
            class="mx-2"
            @click="viewDetails(item)"
          >
            mdi-eye
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <!-- Update Status Dialog -->
    <v-dialog v-model="statusDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h5">{{ $t('Update Status') }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="selectedStatus"
            :items="statusOptions"
            :label="$t('Status')"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="statusDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" text @click="saveStatus">{{ $t('Save Changes') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Update Payment Status Dialog -->
    <v-dialog v-model="paymentStatusDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h5">{{ $t('Update Payment Status') }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="selectedPaymentStatus"
            :items="paymentStatusOptions"
            :label="$t('Payment Status')"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="paymentStatusDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" text @click="savePaymentStatus">{{ $t('Save Changes') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Details View Dialog -->
    <v-dialog v-model="detailsDialog" max-width="600px">
      <v-card v-if="selectedSubscription">
        <v-card-title class="text-h5">{{ $t('Subscription Details') }}</v-card-title>
        <v-card-text>
          <v-list dense>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('Code') }}</v-list-item-title>
                <v-list-item-subtitle>{{ selectedSubscription.subscription_code || 'N/A' }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('Customer') }}</v-list-item-title>
                <v-list-item-subtitle>{{ selectedSubscription.user ? selectedSubscription.user.name : '' }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('Route/Trip') }}</v-list-item-title>
                <v-list-item-subtitle v-if="selectedSubscription.trip && selectedSubscription.trip.route">
                  {{ selectedSubscription.trip.route.name }} ({{ selectedSubscription.trip.channel }})
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('Pickup Stop') }}</v-list-item-title>
                <v-list-item-subtitle>{{ selectedSubscription.pickup_stop ? selectedSubscription.pickup_stop.name : '' }}</v-list-item-subtitle>
                <v-list-item-subtitle class="caption grey--text">{{ selectedSubscription.pickup_stop ? selectedSubscription.pickup_stop.address : '' }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('Dropoff Stop') }}</v-list-item-title>
                <v-list-item-subtitle>{{ selectedSubscription.dropoff_stop ? selectedSubscription.dropoff_stop.name : '' }}</v-list-item-subtitle>
                <v-list-item-subtitle class="caption grey--text">{{ selectedSubscription.dropoff_stop ? selectedSubscription.dropoff_stop.address : '' }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('Selected Time') }}</v-list-item-title>
                <v-list-item-subtitle>{{ selectedSubscription.selected_time || 'N/A' }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('Subscription Plan') }}</v-list-item-title>
                <v-list-item-subtitle>{{ selectedSubscription.subscription_plan ? selectedSubscription.subscription_plan.name : '' }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('Duration') }}</v-list-item-title>
                <v-list-item-subtitle>{{ selectedSubscription.start_date }} {{ $t('to') }} {{ selectedSubscription.end_date }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('Total Price') }}</v-list-item-title>
                <v-list-item-subtitle>{{ selectedSubscription.total_price }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('Status') }}</v-list-item-title>
                <v-list-item-subtitle>
                  <v-chip :color="getStatusColor(selectedSubscription.status)" dark small>
                    {{ $t(selectedSubscription.status) }}
                  </v-chip>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ $t('Payment Status') }}</v-list-item-title>
                <v-list-item-subtitle>
                  <v-chip :color="getPaymentStatusColor(selectedSubscription.payment_status)" dark small>
                    {{ $t(selectedSubscription.payment_status) }}
                  </v-chip>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="detailsDialog = false">{{ $t('Close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  data() {
    return {
      subscriptions: [],
      isLoading: false,
      search: "",
      filterStatus: null,
      filterPaymentStatus: null,
      
      statusDialog: false,
      paymentStatusDialog: false,
      detailsDialog: false,
      
      selectedSubscription: null,
      selectedStatus: "",
      selectedPaymentStatus: "",

      statusFilterOptions: [
        { text: this.$t('All'), value: null },
        { text: this.$t('pending'), value: 'pending' },
        { text: this.$t('active'), value: 'active' },
        { text: this.$t('expired'), value: 'expired' },
        { text: this.$t('cancelled'), value: 'cancelled' },
      ],
      paymentStatusFilterOptions: [
        { text: this.$t('All'), value: null },
        { text: this.$t('pending'), value: 'pending' },
        { text: this.$t('paid'), value: 'paid' },
        { text: this.$t('failed'), value: 'failed' },
        { text: this.$t('refunded'), value: 'refunded' },
      ],

      statusOptions: [
        { text: this.$t('pending'), value: 'pending' },
        { text: this.$t('active'), value: 'active' },
        { text: this.$t('expired'), value: 'expired' },
        { text: this.$t('cancelled'), value: 'cancelled' },
      ],
      paymentStatusOptions: [
        { text: this.$t('pending'), value: 'pending' },
        { text: this.$t('paid'), value: 'paid' },
        { text: this.$t('failed'), value: 'failed' },
        { text: this.$t('refunded'), value: 'refunded' },
      ],

      headers: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Code", value: "subscription_code" },
        { text: "Customer", value: "user" },
        { text: "Route/Trip", value: "trip" },
        { text: "Pickup Stop", value: "pickup_stop" },
        { text: "Dropoff Stop", value: "dropoff_stop" },
        { text: "Selected Time", value: "selected_time" },
        { text: "Plan", value: "plan" },
        { text: "Start/End Date", value: "dates", sortable: false },
        { text: "Status", value: "status" },
        { text: "Payment Status", value: "payment_status" },
        { text: "Total Price", value: "total_price" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    footerProps() {
      return {
        itemsPerPageText: this.$t("rows_per_page"),
      }
    }
  },
  mounted() {
    this.loadSubscriptions();
  },
  methods: {
    loadSubscriptions() {
      this.isLoading = true;
      const params = {};
      if (this.filterStatus !== null && this.filterStatus !== '') {
        params.status = this.filterStatus;
      }
      if (this.filterPaymentStatus !== null && this.filterPaymentStatus !== '') {
        params.payment_status = this.filterPaymentStatus;
      }

      axios
        .get(`/trip-subscriptions/all`, { params })
        .then((response) => {
          this.subscriptions = response.data;
        })
        .catch((error) => {
          this.$notify({
            title: this.$t("Error"),
            text: this.$t("error_retrieving_subscriptions"),
            type: 'error'
          });
          console.log(error);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    getStatusColor(status) {
      switch (status) {
        case 'active':
          return 'success';
        case 'pending':
          return 'warning';
        case 'expired':
          return 'grey';
        case 'cancelled':
          return 'error';
        default:
          return 'grey';
      }
    },
    getPaymentStatusColor(status) {
      switch (status) {
        case 'paid':
          return 'success';
        case 'pending':
          return 'warning';
        case 'failed':
          return 'error';
        case 'refunded':
          return 'info';
        default:
          return 'grey';
      }
    },
    openStatusDialog(item) {
      this.selectedSubscription = item;
      this.selectedStatus = item.status;
      this.statusDialog = true;
    },
    openPaymentStatusDialog(item) {
      this.selectedSubscription = item;
      this.selectedPaymentStatus = item.payment_status;
      this.paymentStatusDialog = true;
    },
    saveStatus() {
      this.isLoading = true;
      this.statusDialog = false;
      axios
        .post(`/trip-subscriptions/update-status`, {
          id: this.selectedSubscription.id,
          status: this.selectedStatus
        })
        .then(() => {
          this.selectedSubscription.status = this.selectedStatus;
          this.$notify({
            title: this.$t("Success"),
            text: this.$t("status_updated_successfully"),
            type: 'success'
          });
          this.loadSubscriptions();
        })
        .catch((error) => {
          this.$notify({
            title: this.$t("Error"),
            text: this.$t("status_update_failed"),
            type: 'error'
          });
          console.log(error);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    savePaymentStatus() {
      this.isLoading = true;
      this.paymentStatusDialog = false;
      axios
        .post(`/trip-subscriptions/update-payment-status`, {
          id: this.selectedSubscription.id,
          payment_status: this.selectedPaymentStatus
        })
        .then(() => {
          this.selectedSubscription.payment_status = this.selectedPaymentStatus;
          this.$notify({
            title: this.$t("Success"),
            text: this.$t("payment_status_updated_successfully"),
            type: 'success'
          });
          this.loadSubscriptions();
        })
        .catch((error) => {
          this.$notify({
            title: this.$t("Error"),
            text: this.$t("payment_status_update_failed"),
            type: 'error'
          });
          console.log(error);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    viewDetails(item) {
      this.selectedSubscription = item;
      this.detailsDialog = true;
    }
  }
};
</script>
